// Markdown Preview Wrapper Component
// Wraps a textarea and adds Preview/Raw tabs
import { insertTextAtCursor } from '../utils/text-insertion.js';
import { applyPostStyleTokens, clearPostStyleTokens } from '../utils/post-style-applier.js';
import { escapeHtml } from '../utils/html-utils.js';
import MediaAttachment from './media-attachment.js';

class MarkdownPreview extends HTMLElement {
  constructor() {
    super();
    this.mode = 'raw'; // 'raw' or 'preview'
    this.textarea = null;
    this.previewContainer = null;
    this._titleInput = null;
    this._onTitleInput = null;
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
  }

  disconnectedCallback() {
    // Clean up event listener
    if (this._filesSelectedHandler) {
      document.removeEventListener('files-selected', this._filesSelectedHandler);
      this._filesSelectedHandler = null;
    }
    if (this._titleInput && this._onTitleInput) {
      this._titleInput.removeEventListener('input', this._onTitleInput);
      this._titleInput = null;
      this._onTitleInput = null;
    }
  }

  bindTitleInput(inputElement) {
    if (this._titleInput && this._onTitleInput) {
      this._titleInput.removeEventListener('input', this._onTitleInput);
    }
    this._titleInput = inputElement || null;
    if (!this._titleInput) {
      this._onTitleInput = null;
      return;
    }
    this._onTitleInput = () => {
      if (this.mode === 'preview') {
        this.updatePreview();
      }
    };
    this._titleInput.addEventListener('input', this._onTitleInput);
  }

  _getPreviewTitleHtml() {
    const title = this._titleInput?.value?.trim() || '';
    if (!title || title === 'Untitled Post') {
      return '';
    }
    return `<div class="markdown-preview-title post-title"><h2>${escapeHtml(title)}</h2></div>`;
  }

  render() {
    // Find the textarea (should be a child)
    this.textarea = this.querySelector('textarea');
    if (!this.textarea) {
      window.logger.warn('markdown-preview: No textarea found');
      return;
    }

    // Store original textarea attributes and value
    const originalClass = this.textarea.className;
    const originalId = this.textarea.id;
    const originalName = this.textarea.name;
    const originalPlaceholder = this.textarea.placeholder;
    const originalRows = this.textarea.rows;
    const originalRequired = this.textarea.required;
    const originalValue = this.textarea.value;

    // Store all other attributes
    const otherAttributes = {};
    Array.from(this.textarea.attributes).forEach(attr => {
      if (!['class', 'id', 'name', 'placeholder', 'rows', 'required', 'value'].includes(attr.name)) {
        otherAttributes[attr.name] = attr.value;
      }
    });

    // Create wrapper structure
    const wrapper = document.createElement('div');
    wrapper.className = 'markdown-preview-wrapper';
    wrapper.innerHTML = `
      <div class="markdown-preview-tabs">
        <button type="button" class="preview-tab active" data-mode="raw">Raw</button>
        <button type="button" class="preview-tab" data-mode="preview">Preview</button>
      </div>
      <div class="markdown-toolbar-container">
        <markdown-toolbar show-sparkle="true"></markdown-toolbar>
      </div>
      <div class="markdown-preview-content">
        <div class="markdown-textarea-container">
          <textarea 
            class="${originalClass}" 
            id="${originalId}"
            name="${originalName}"
            placeholder="${originalPlaceholder}"
            rows="${originalRows}"
            ${originalRequired ? 'required' : ''}
          >${originalValue}</textarea>
        </div>
        <div class="markdown-preview-container" style="display: none;"></div>
      </div>
    `;

    // Replace textarea with wrapper
    this.textarea.replaceWith(wrapper);

    // Get references
    this.textarea = wrapper.querySelector('textarea');
    this.previewContainer = wrapper.querySelector('.markdown-preview-container');
    this.rawTab = wrapper.querySelector('[data-mode="raw"]');
    this.previewTab = wrapper.querySelector('[data-mode="preview"]');
    this.toolbar = wrapper.querySelector('markdown-toolbar');

    // Copy any additional attributes
    Object.entries(otherAttributes).forEach(([name, value]) => {
      this.textarea.setAttribute(name, value);
    });
  }

  attachEventListeners() {
    if (!this.rawTab || !this.previewTab) return;

    this.rawTab.addEventListener('click', () => {
      this.setMode('raw');
    });

    this.previewTab.addEventListener('click', () => {
      this.setMode('preview');
    });

    // Handle Sparkle button click
    this.addEventListener('toolbar-sparkle-click', () => {
      this.openMediaSourcePicker();
    });

    // Listen for files-selected event from media source picker
    // Store handler reference for cleanup
    this._filesSelectedHandler = (e) => {
      // Use textarea from event detail if provided and it's within this component
      // Otherwise, use this component's textarea if no textarea was specified
      let targetTextarea;
      if (e.detail?.textarea) {
        // If event has a textarea, only use it if it's within this component
        if (this.contains(e.detail.textarea)) {
          targetTextarea = e.detail.textarea;
        } else {
          // Textarea is for a different component, ignore this event
          return;
        }
      } else {
        // No textarea in event, use this component's textarea
        targetTextarea = this.textarea;
      }

      if (targetTextarea) {
        this.handleFilesSelected(e.detail.files, targetTextarea);
      }
    };
    document.addEventListener('files-selected', this._filesSelectedHandler);

    // Update preview when textarea content changes
    if (this.textarea) {
      this.textarea.addEventListener('input', () => {
        if (this.mode === 'preview') {
          this.updatePreview();
        }
      });
    }
  }

  setMode(mode) {
    if (this.mode === mode) return;

    this.mode = mode;
    const textareaContainer = this.querySelector('.markdown-textarea-container');
    const previewContainer = this.querySelector('.markdown-preview-container');
    const toolbarContainer = this.querySelector('.markdown-toolbar-container');

    // Hide/show the toolbar
    if (toolbarContainer) {
      if (mode === 'preview') {
        toolbarContainer.style.display = 'none';
      } else {
        toolbarContainer.style.display = 'block';
      }
    }

    if (mode === 'raw') {
      this.rawTab.classList.add('active');
      this.previewTab.classList.remove('active');
      if (textareaContainer) textareaContainer.style.display = 'block';
      if (previewContainer) previewContainer.style.display = 'none';
    } else {
      this.rawTab.classList.remove('active');
      this.previewTab.classList.add('active');
      if (textareaContainer) textareaContainer.style.display = 'none';
      if (previewContainer) {
        previewContainer.style.display = 'block';
        this.updatePreview();
      }
    }
  }

  async updatePreview() {
    if (!this.textarea || !this.previewContainer) return;

    const markdown = this.textarea.value;
    const titleHtml = this._getPreviewTitleHtml();

    if (!markdown.trim() && !titleHtml) {
      this.previewContainer.innerHTML = '<p class="markdown-empty">Start typing to see preview...</p>';
      return;
    }

    if (!markdown.trim()) {
      this.previewContainer.innerHTML = titleHtml;
      return;
    }

    // Show loading state
    this.previewContainer.innerHTML = `${titleHtml}<p class="markdown-loading">Rendering preview...</p>`;

    try {
      // Call backend API to render markdown
      const response = await window.API.request('/markdown/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ markdown })
      });

      const bodyHtml = response.html || '<p class="markdown-empty">No preview available</p>';
      this.previewContainer.innerHTML = titleHtml + bodyHtml;
    } catch (error) {
      window.logger?.error('Error rendering markdown preview:', error);
      this.previewContainer.innerHTML = `${titleHtml}<p class="markdown-error">Error rendering preview. Please try again.</p>`;
    }
  }

  applyPostStyle(style) {
    if (!this.previewContainer) return;
    applyPostStyleTokens(this.previewContainer, style);
    if (this.mode === 'preview') {
      this.updatePreview();
    }
  }

  clearPostStyle() {
    if (!this.previewContainer) return;
    clearPostStyleTokens(this.previewContainer);
  }


  getTextarea() {
    return this.textarea;
  }

  getToolbar() {
    return this.toolbar;
  }

  openMediaSourcePicker() {
    // Get or create the media source picker component
    let picker = document.querySelector('media-source-picker');
    if (!picker) {
      picker = document.createElement('media-source-picker');
      document.body.appendChild(picker);
    }
    picker._targetTextarea = this.textarea;
    picker.open();
  }

  handleFilesSelected(files, textarea) {
    if (!files || files.length === 0) return;

    // Insert markdown syntax for each selected file
    files.forEach((file, index) => {
      // Convert OAuth URL to markdown syntax
      // file.fileUrl is in format: oauth://provider/accountId/fileId
      const fileUrl = file.fileUrl || `oauth://${file.provider}/${file.accountId}/${file.fileId}`;
      const fileName = file.fileName || file.name || `File from ${file.provider}`;

      // Check if file is an image based on mimeType
      const mimeType = MediaAttachment.normalizeContentType(file.mimeType);
      const isImage = mimeType && mimeType.startsWith('image/');

      let markdown;
      if (isImage) {
        // Use image markdown syntax for images
        markdown = `![${fileName}](${fileUrl})`;
      } else {
        // Use custom syntax for non-image files that will be rendered as media-attachment
        // Format: [file:filename](oauth://provider/account/fileId?mimeType=...)
        const urlWithMimeType = file.mimeType ? `${fileUrl}?mimeType=${encodeURIComponent(file.mimeType)}` : fileUrl;
        markdown = `[file:${fileName}](${urlWithMimeType})`;
      }

      // Insert with spacing
      const prefix = index === 0 ? '\n\n' : '\n';
      insertTextAtCursor(textarea, `${prefix}${markdown}`, { ensureTrailingNewline: false });
    });

    // Focus the textarea and trigger input event
    textarea.focus();
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }

}

customElements.define('markdown-preview', MarkdownPreview);

